import React, { useCallback } from 'react';
import { useDocumentContext } from '../../contexts/DocumentContext';
import { useManagerContext } from '../../contexts/ManagerContext';
import { useEnhancedAuth } from '../../contexts/EnhancedAuthContext';
import { useDocumentManagementView } from '../../hooks/useDocumentManagementView';
import { useAutoSelectUser } from '../../hooks/useAutoSelectUser';
import { UserDetails } from '../../types/User';
import { PermissionGate } from '../common/PermissionGate';
import ErrorBoundary from '../ErrorBoundary';
import styles from '../../styles/DocumentManagement.module.css';
import DocumentsOverview from '../items/DocumentManagement/DocumentsOverview';
import UserDocuments from '../items/DocumentManagement/UserDocuments';
import UserReminders from '../items/DocumentManagement/UserReminders';
import CompanyDocuments from '../items/DocumentManagement/CompanyDocuments';
import AgentsList from '../items/DocumentManagement/AgentsList';
import AgentCompletionStatus from '../items/DocumentManagement/AgentCompletionStatus';

interface DocumentManagementProps {
    userDetails: UserDetails | null;
    userReady?: boolean;
    onLogout: () => void;
}

const DocumentManagement: React.FC<DocumentManagementProps> = React.memo(({ userDetails, userReady, onLogout }) => {
  const { hasPermission } = useEnhancedAuth();

  // Use custom hook for view management
  const {
    currentView,
    navAndHighlightNavigate,
  } = useDocumentManagementView({ userDetails, userReady, onLogout });
  
  // Get document context data
  const {
    selectedUser,
    categoriesWithStats,
    overallStats,
    structuralLoading,
    error: documentError,
    categories: categoriesMetadata,
    documentTypes,
    documentFields,
  } = useDocumentContext();

  // Build reminder click handler using the structural map for category/subcategory lookup
  const handleReminderClick = useCallback((reminder: any) => {
    const docType = reminder._resolvedType ?? null;
    const field = reminder._resolvedField ?? null;
    // For agency reminders the profiles join gives us the receiver's id and name
    const user = reminder.profiles
      ? { id: reminder.profiles.id, fullNames: reminder.profiles.full_names, lastName: reminder.profiles.last_name } as any
      : selectedUser;
    navAndHighlightNavigate(user, docType ?? {}, field ?? {});
  }, [documentTypes, documentFields, selectedUser, navAndHighlightNavigate]);

  // Get manager context data for company documents
  const {
    companyCategories,
    companyCategoriesWithStats,
    companyOverallStats,
    companyFieldValues,
    getCompanySubcategoriesForCategory,
    getCompanyDocumentTypesForSubcategory,
    getCompanyFieldsForSubcategory,
    saveCompanyFieldValues,
    fetchCompanyFieldValues,
    loading: managerLoading,
    error: managerError
  } = useManagerContext();

  // Auto-select user if not manager (using custom hook to fix dependency warnings)
  useAutoSelectUser({ userReady: userReady || false, userDetails, hasPermission });

  // If not ready, show loading
  if (!userReady) {
    return <div>Loading...</div>;
  }

  // Show document data error state
  if (documentError) {
    return (
      <div className={styles.documentManagementContainer}>
        <div className={styles.documentManagementHeader}>
          <h1 className={styles.documentManagementTitle}>Document Management</h1>
          <div className={styles.errorContainer}>
            <div className={styles.textError}>Error loading document data: {documentError}</div>
            <div className={styles.errorDetails}>
              <p><strong>Debug Info:</strong></p>
              <p>User ID: {userDetails?.id || 'Not provided'}</p>
              <p>Agency ID: {userDetails?.agency?.agencyId || 'Not provided'}</p>
              <p>Role: {userDetails?.role || 'Not provided'}</p>
              <p>Check browser console for detailed error logs.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.documentManagementContainer}>

      <div className={styles.documentManagementContent}>
        {currentView === 'overview' && (
          <ErrorBoundary componentName="DocumentsOverview">
            <DocumentsOverview userDetails={userDetails} onReminderClick={handleReminderClick} />
            <PermissionGate requiredPermissions={['user_manage_agency']}>
              {userDetails?.agency?.agencyId && (
                <ErrorBoundary componentName="AgentCompletionStatus">
                  <AgentCompletionStatus />
                </ErrorBoundary>
              )}
            </PermissionGate>
          </ErrorBoundary>
        )}
        {currentView === 'docs' && (
          <ErrorBoundary componentName="UserDocuments">
            <UserDocuments
              userDetails={userDetails}
              selectedUser={selectedUser}
              overallStats={overallStats}
              categoriesWithStats={categoriesWithStats}
              categoriesMetadata={categoriesMetadata}
              canManageUsers={hasPermission('user_manage_agency')}
              documentLoading={structuralLoading}
              documentError={documentError}
              AgentsList={AgentsList}
            />
          </ErrorBoundary>
        )}
        {currentView === 'company-docs' && (
          <ErrorBoundary componentName="CompanyDocuments">
            <CompanyDocuments
              userDetails={userDetails}
              overallStats={companyOverallStats}
              categoriesWithStats={companyCategoriesWithStats}
              categoriesMetadata={companyCategories || []}
              documentLoading={managerLoading}
              documentError={managerError}
              getSubcategoriesForCategory={getCompanySubcategoriesForCategory}
              getDocumentTypesForSubcategory={getCompanyDocumentTypesForSubcategory}
              getFieldsForSubcategory={getCompanyFieldsForSubcategory}
              saveCompanyFieldValues={saveCompanyFieldValues}
              fetchCompanyFieldValues={fetchCompanyFieldValues}
              companyFieldValues={companyFieldValues}
            />
          </ErrorBoundary>
        )}
        {currentView === 'reminders' && (
          <ErrorBoundary componentName="UserReminders">
            <UserReminders onReminderClick={handleReminderClick} />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
});

export default DocumentManagement;
